/**
 * One removable chip in the active-filter strip.
 *
 * The whole chip is the remove button - there is no separate ✕ target - so
 * `ariaLabel` carries the action ("Remove domain filter React"), not the value.
 * The visible label is the value and is truncated at 160px; the accessible name
 * is not, which is the point of keeping them separate props.
 *
 * Was declared twice, byte-for-byte identically, in `MindspaceFilterPanel` and
 * `QuickBytesFilterPanel`.
 */

import { X } from 'lucide-react';
import { cn } from '@ai-tutor/ui';

export interface SummaryChipProps {
  icon: React.ReactNode;
  label: string;
  onRemove: () => void;
  ariaLabel: string;
  className?: string;
}

export function SummaryChip({
  icon,
  label,
  onRemove,
  ariaLabel,
  className,
}: SummaryChipProps) {
  return (
    <button
      type='button'
      onClick={onRemove}
      aria-label={ariaLabel}
      className={cn(
        'dark:tint-brand inline-flex max-w-full items-center gap-1 rounded-full border border-brand-200 bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 transition-colors hover:bg-brand-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 dark:border-primary/60 dark:hover:bg-primary/50 dark:focus-visible:ring-primary/40',
        className
      )}
    >
      <span className='text-brand-500 dark:text-primary'>{icon}</span>
      <span className='max-w-[160px] truncate'>{label}</span>
      <X className='h-2.5 w-2.5 opacity-60' />
    </button>
  );
}
